import { useCallback, useRef } from 'react';

function valuesEqual(left, right) {
  if (Object.is(left, right)) return true;
  if (typeof left !== typeof right) return false;
  if (left == null || right == null) return false;
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
      return false;
    }
    return left.every((value, index) => valuesEqual(value, right[index]));
  }
  if (typeof left !== 'object') return false;

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => (
    Object.prototype.hasOwnProperty.call(right, key)
    && valuesEqual(left[key], right[key])
  ));
}

/**
 * Reconcile a live Firestore snapshot with a rule form's local draft.
 *
 * A same-id snapshot may contain an out-of-band change (for example the
 * administrator attention banner deactivating a rule). Untouched fields adopt
 * those changes immediately. Fields changed locally remain owned by the draft
 * until a snapshot acknowledges the same value, so an older listener emission
 * cannot overwrite an edit that is still being autosaved.
 */
export function useRuleFormSnapshotSync(initialData, setData) {
  const previousIncomingRef = useRef(initialData);
  const dirtyFieldsRef = useRef(new Set());

  const markLocalChanges = useCallback((updates) => {
    Object.keys(updates || {}).forEach((field) => dirtyFieldsRef.current.add(field));
  }, []);

  const reconcileIncomingData = useCallback((incomingData) => {
    const previousIncoming = previousIncomingRef.current;
    const identityChanged = incomingData?.id !== previousIncoming?.id;

    setData((currentData) => {
      if (identityChanged) {
        dirtyFieldsRef.current.clear();
        return incomingData;
      }

      let changed = false;
      const nextData = { ...currentData };
      const fields = new Set([
        ...Object.keys(previousIncoming || {}),
        ...Object.keys(incomingData || {}),
      ]);

      fields.forEach((field) => {
        const previousValue = previousIncoming?.[field];
        const incomingValue = incomingData?.[field];
        const currentValue = currentData?.[field];
        const isDirty = dirtyFieldsRef.current.has(field);

        if (isDirty && valuesEqual(currentValue, incomingValue)) {
          dirtyFieldsRef.current.delete(field);
        }

        if (!valuesEqual(previousValue, incomingValue)
          && !dirtyFieldsRef.current.has(field)
          && !valuesEqual(currentValue, incomingValue)) {
          nextData[field] = incomingValue;
          changed = true;
        }
      });

      return changed ? nextData : currentData;
    });

    previousIncomingRef.current = incomingData;
  }, [setData]);

  return { markLocalChanges, reconcileIncomingData };
}
